/* eslint-disable max-len */
import { Button, Checkbox, CircularProgress, Divider, IconButton, ListItemIcon, makeStyles, Menu, MenuItem, Paper, Table, TableBody, TableCell,
    TableHead, TableRow, Theme, Typography } from '@material-ui/core';
import { SortOrder, Entity, EntityMetricsDefinition, EventLogRecord, EntityModuleType } from '@moderepo/mode-apis';
import { capitalizeAll, FontIcon, GenericMessageBox, ITEMS_PER_PAGE_10, useDropdown, useModePanelStyle, useModeTableStyle, Time,
    SELECT_INPUT_OPTION_DIVIDER, SELECT_INPUT_OPTION_DIVIDER_TYPE } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import produce, { Draft } from 'immer';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TFunction } from 'react-i18next';
import moment from 'moment';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { EventFilterOption, findMetricInfo, getEventMetricDisplayName, getEventMetricDisplayValue, EventMetricSettings, EventState,
    isEventFilterOption, useGetEventLogData, useConvertTimeControlParamsToFetchDataTimeRangeParams } from '.';
import { EntityUIComponentCollection, getMetricsDefinitionMetricsId, useSelectTranslatedEntityClasses } from '..';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectAllEntityChildren, useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingNumberValue, isValidSettingStringValue }
    from '../../../utils/utils';
import { getDateTimeDisplay } from '../../containerUtils';
import { EntityUIComponentWithChildrenProps } from '../EntityDetailsLayouts';
import { isEventMetricSettings } from './utils';
import { NO_VALUE } from '../../../utils/constants';
import { SettingsErrorLogger } from '../../../utils/logger';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        panelHeader: {
            padding: theme.spacing(1),
        },

        panelContent: {
            overflow: 'auto',
            padding : 0,
        },

        tableBodyRow: {
            cursor       : 'pointer',
            '&.highlight': {
                [`&.${EventState.NEUTRAL}`]: {
                    '& $tableCol': {
                        background: theme.palette.info.main,
                        color     : theme.palette.info.contrastText,
                    },
                },
                [`&.${EventState.GOOD}`]: {
                    '& $tableCol': {
                        background: theme.palette.success.main,
                        color     : theme.palette.success.contrastText,
                    },
                },
                [`&.${EventState.SERIOUS}`]: {
                    '& $tableCol': {
                        background: theme.palette.warning.main,
                        color     : theme.palette.warning.contrastText,
                    },
                },
                [`&.${EventState.CRITICAL}`]: {
                    '& $tableCol': {
                        background: theme.palette.error.main,
                        color     : theme.palette.error.contrastText,
                    },
                },
            },
        },

        tableCol: {
            '&.timestamp': {
                width: 'fit-content',
            },
            '&.entity-name': {
                whiteSpace: 'normal',
            },
            '&.metric-value': {
                textAlign: 'center',
            },
            '&.highlight': {
                '& .value': {
                    borderRadius: 20,
                    padding     : theme.spacing(0, 1),
                    fontWeight  : 'bold',
                },
                [`&.${EventState.NEUTRAL}`]: {
                    '& .value': {
                        background: theme.palette.info.main,
                        color     : theme.palette.info.contrastText,
                    },
                },
                [`&.${EventState.GOOD}`]: {
                    '& .value': {
                        background: theme.palette.success.main,
                        color     : theme.palette.success.contrastText,
                    },
                },
                [`&.${EventState.SERIOUS}`]: {
                    '& .value': {
                        background: theme.palette.warning.main,
                        color     : theme.palette.warning.contrastText,
                    },
                },
                [`&.${EventState.CRITICAL}`]: {
                    '& .value': {
                        background: theme.palette.error.main,
                        color     : theme.palette.error.contrastText,
                    },
                },
            },
        },

        loadMoreButtonContainer: {
            width         : '100%',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            padding       : theme.spacing(3),
        },

        loadMoreButton: {
        },

        filterOptionLabel: {
            textTransform: 'capitalize',
        },

        [theme.breakpoints.up('md')]: {
            panelHeader: {
                padding: theme.spacing(2),
            },
        },
    };
}, { name: 'EntityEventsTableUIComponent', index: 1 });



/**
 * When we specify an array of metrics to show in the event table, we need to provide some info about the metrics so that the component
 * know how to find these metrics from the metricsDefinition. We can use the 'EventMetricSettings' interface for this HOWEVER, we needed
 * additional attributes to customize the CSS style of the display values therefore, we will create a new interface 'ExtEventMetricSettings'
 * which extends EventMetricSettings
 */
interface ExtEventMetricSettings extends EventMetricSettings {

    // The CSS style to apply to the header table cell
    readonly headerCellStyle?: CSSProperties | undefined;

    // The CSS style to apply to the Table cell that display the value element
    readonly valueCellStyle?: CSSProperties | undefined;

    // The CSS style to apply to the value element
    readonly valueStyle?: CSSProperties | undefined;
}



/**
 * Type guard function to check if an object is a type of ExtEventMetricSettings
 */
const isExtEventMetricSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is ExtEventMetricSettings => {

    const settings = obj as ExtEventMetricSettings;
    const configPath = [...(currentConfigPath ?? [])];

    // ExtEventMetricSettings extends EventMetricSettings therefore we need to call 'isEventMetricSettings' to make
    // sure it has the required attributes for a EventMetricSettings
    if (!isEventMetricSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // style objects are optional but if they are provided, they must be objects
    if (settings.headerCellStyle !== undefined && !isObjectLiteral(settings.headerCellStyle)) {
        return errorLogger?.logError({
            message: '"headerCellStyle" must be undefined or an object',
            value  : settings.headerCellStyle,
            path   : [...configPath, 'headerCellStyle'],
        }) ?? false;
    }
    if (settings.valueCellStyle !== undefined && !isObjectLiteral(settings.valueCellStyle)) {
        return errorLogger?.logError({
            message: '"valueCellStyle" must be undefined or an object',
            value  : settings.valueCellStyle,
            path   : [...configPath, 'valueCellStyle'],
        }) ?? false;
    }
    if (settings.valueStyle !== undefined && !isObjectLiteral(settings.valueStyle)) {
        return errorLogger?.logError({
            message: '"valueStyle" must be undefined or an object',
            value  : settings.valueStyle,
            path   : [...configPath, 'valueStyle'],
        }) ?? false;
    }

    return true;
};



/**
 * EventLogRecord data structure DOES NOT have any attribute that map the record to an entity. They have 'searchKeys' which can be used to
 * map to an entity. We need to map EventLogRecords to entity objects in many places which can be a lot of work to look up entity by searchKey
 * therefore, we will create a new interface 'EntityEventLogRecord' that extends 'EventLogRecord' AND add a new attribute 'entityId'. We can
 * attach the entityId to the EventLogRecord so that we don't need to keep looking up entity object by searchKey.
 */
interface EntityEventLogRecord extends EventLogRecord {
    readonly entityId: string;
}


/**
 * The object containing the event record info that are ready for display
 */
interface LocalizedEventLogRecord {
    readonly entityId: string;
    readonly entityName: string;
    readonly displayTimestamp: string;
    readonly timestamp: string | undefined;

    readonly displayValuePerMetricsDefinition: {
        readonly [metricsDefId: string]: {
            readonly [metricName: string]: string | undefined;
        } | undefined;
    };

    readonly statePerMetricsDefinition: {
        readonly [metricsDefId: string]: {
            readonly [metricName: string]: EventState | undefined;
        }
    };

    // The state for the entire record. This state is derived from the states of each individual metric using the MOST SEVERE metric's state
    readonly recordAggregatedState?: EventState | undefined;
}



/**
 * Given an event Record, return an object that has all the necessary attributes and metric value localized ready for display. Here is an example
 * output
 *      {
 *          timestamp: "Aug 25, 2022, 03:58:02 PDT",
 *          entityName: "Demo Driver",
 *          displayValuePerMetric: {
 *              result: "Passed",
 *              value: "0.001 mg/L"
 *          },
 *          statePerMetric: {
 *              result: "good",
 *          },
 *          recordAggregatedState: "good"
 *      }
 */
const createLocalizedEventLogRecord = (
    record: EntityEventLogRecord | undefined,
    entity: Entity,
    metricsDefinitions: readonly EntityMetricsDefinition[] | undefined,
    customSettings: EntityEventsTableUIComponentCustomSettings,
    trans: TFunction,
): LocalizedEventLogRecord => {

    const valuesPerMetricsDefinition: {
        [metricsDefId: string]: {
            [metricName: string]: string;
        }
    } = {};

    const statePerMetricsDefinition: {
        [metricsDefId: string]: {
            [metricName: string]: EventState | undefined;
        }
    } = {};

    // Store all the metrics' state in this array so that we can use this to find the aggregated state later
    const allStates: EventState[] = [];


    Object.entries(customSettings.viewSettingsPerMetricsDefinition).forEach(([metricsDefinitionId, metricsViewSettings]) => {

        const showMetrics = metricsViewSettings?.showMetrics;
        const valuePerMetric = {};
        valuesPerMetricsDefinition[metricsDefinitionId ?? ''] = valuePerMetric;

        const statePerMetric = {};
        statePerMetricsDefinition[metricsDefinitionId ?? ''] = statePerMetric;

        showMetrics?.forEach((metricSettings) => {


            // metricSettings can be a string or an Object of type "EventMetricSettings". If it is a string, convert it to an
            // EventMetricSettings so that we don't have to keep checking if it is a string or EventMetricSettings every time we use it.
            const metricSettingsObj: EventMetricSettings = typeof metricSettings === 'string' ? {
                name: metricSettings,
            } : metricSettings;


            if (record) {
                // Get the value from the record's fields and then format it based on the metric and metricSettingsObj
                const metricsDefinition = metricsDefinitions?.find((metricsDef) => {
                    return metricsDef.metricsDefinitionId === metricsDefinitionId;
                });
                const metric = metricsDefinition ? findMetricInfo(metricsDefinition, metricSettingsObj) : undefined;

                const metricValue = record.fields[metricSettingsObj.name];
                const formattedMetricValue = metric ? getEventMetricDisplayValue(metricValue, metric, metricSettingsObj, trans) : metricValue;
                if (formattedMetricValue) {
                    valuePerMetric[metricSettingsObj.name] = formattedMetricValue;
                } else {
                    valuePerMetric[metricSettingsObj.name] = formattedMetricValue ?? customSettings.defaultNoValueDisplayKey ?? NO_VALUE;
                }

                // If there is a valueToEventStateMapping for the value then we can get the metric's state based on the mapping
                const metricState = metricSettingsObj.valueToEventStateMapping?.[metricValue ?? ''];
                if (metricState) {
                    statePerMetric[metricSettingsObj.name] = metricState;
                    allStates.push(metricState);
                }
            } else {
                // If there is no record, use the customSetting's to get the metric's value and state
                // Use the CUSTOM displayValueKey from the "displayValueKeyPerMetric" first if it exists.
                // If not, use the default default display key from "defaultNoValueDisplayKey"
                const displayValueKey = metricSettingsObj.noValueDisplayKey ?? customSettings.defaultNoValueDisplayKey ?? NO_VALUE;
                valuePerMetric[metricSettingsObj.name] = trans(displayValueKey);

                // Use the CUSTOM metric state from the "noValueState" first if it exists.
                // If not, use the default no value state from "defaultNoValueState"
                const metricState = metricSettingsObj.noValueState ?? metricsViewSettings?.defaultNoValueState;
                if (metricState) {
                    statePerMetric[metricSettingsObj.name] = metricState;
                    allStates.push(metricState);
                }
            }
        });
    });


    // Sort the allStates and then reverse the array so that the MOST severe state will be FIRST and then use the FIRST state
    const aggregatedState = allStates.sort().reverse()[0];


    return {
        entityId                        : entity.entityId,
        timestamp                       : record?.timestamp,
        displayTimestamp                : record ? getDateTimeDisplay(record.timestamp, trans, true) : '-',
        entityName                      : entity.displaySettings.name,
        recordAggregatedState           : aggregatedState,
        displayValuePerMetricsDefinition: valuesPerMetricsDefinition,
        statePerMetricsDefinition,
    };
};



/**
 * Interface for EntityEventsTableUIComponent's customSettings. These are the props/value that can be
 * configured for the 'EntityEventsTableUIComponent' from the entityClass's uiSettings.
 */
export interface EntityEventsTableUIComponentCustomSettings {

    // The title to display in the header. This can be a translation key but the admin will need to add the translation for this key
    // in the project's translationOverride settings
    readonly titleKey: string;

    // The entity class of the children we want to show. This component can only show events of children of 1 entity class. It doesn't
    /// make sense to support multiple entity classes because each entity class has different events and different metrics therefore
    // they can't be shown in the same UI. To show children events of a different entity classes, we can always create another instance
    // of this component.
    readonly childrenClass?: string | undefined;

    readonly viewSettingsPerMetricsDefinition: {
        readonly [metricsDefId: string]: {
            /**
             * To specify the list of metrics and their values we want to show in the table. The items in the array can be either just the metrics' names
             * or objects containing the metrics' names and some settings e.g. styles.
             *    customSettings: {
             *        metricsDefinitionId: "abcdef",
             *
             *        // Show 2 metrics, "result", and "value". For "value", there is no other settings therefore we can just specify the metricName.
             *        // For "result", because we want to highlight the value, we need to configure the "valueToEventStateMapping"
             *        showMetrics: [
             *            {
             *                name: "result",
             *                valueToEventStateMapping: {
             *                    0: "GOOD",
             *                    1: "CRITICAL",
             *                    9: "SERIOUS"
             *                }
             *            },
             *            "value",
             *        ]
             *    }
             */
            readonly showMetrics: readonly (string | EventMetricSettings)[];

            // The default event state to use if a metric has no value. For example, if we want to automatically consider a metric is in CRITICAL
            // state when there is no value then we can set this attribute to "critical". This will apply to ALL metrics that don't have value.
            readonly defaultNoValueState?: EventState | undefined;
        } | undefined;
    };

    /**
     * The default filters to apply when fetching data. The metrics specified in the filters should be one of the metrics in the
     * "showMetrics" settings. e.g. filters='AND(result==="1", value>=0.1234)'
     * This will only applied if there is no filter selected by the user from the "filters" option
     */
    readonly defaultFiltersPerMetricsDefinition: {
        readonly [metricsDefId: string]: string | undefined;
    } | undefined;

    // Whether or not to show the table header row
    readonly showTableHeader?: boolean | undefined;

    // The style to apply to the container that displays the events. This apply to only the element that contain the event rows, not the
    // component
    readonly eventsContainerStyle?: CSSProperties | undefined;

    // The custom pagination limit to use when fetching events. The default is 10. The range is any number between 5 and 100.
    readonly fetchEventsLimit?: number | undefined;

    // To set the sort order of the events. The default will be DESC (latest events show first) if this is not provided.
    readonly eventsSortOrder?: SortOrder | undefined;

    // Whether or not to highlight the entire row based on the record's state for each row. Default is FALSE.
    readonly highlightRow?: boolean | undefined;

    // Sometimes 1 entity has more than 1 event records and we want to group them and show them next to each other. By default, we WILL NOT
    // group records. Records will be sorted by timestamp. If this setting is true, records are still sorted by timestamp HOWEVER, they will
    // be grouped and then sort by timestamp.
    readonly groupRecordsByEntity?: boolean | undefined;

    // Whether or not to show the entity's name. The default is TRUE unless specifically set to false
    readonly showEntityName?: boolean | undefined;

    /**
     * Whether or not to hide entity name if the name is already displayed. Entities can have more than 1 records and if "groupRecordsByEntity"
     * is true, the records for the same entity will show up next to each other. The entity name will be displayed in multiple rows and it can
     * be too much. Therefore, we can use this attribute to hide entity names that repeat. This only apply to CONSECUTIVE records that belonged
     * to the same entity. If 2 records that belongs to the same entity but are NOT next to each other, the name will still be shown in both rows.
     * The default value is FALSE, we will show the entity names even if they repeat.
     */
    readonly hideRepeatedEntityName?: boolean | undefined;

    // The string to use for all metrics that don't have value. For example, if we want to show "-" for all metrics that don't have value,
    // set this attribute to "-". This can be a translation key or just a string. If it is a translation key, the admin must add the
    // translation in the project's translationOverride.
    readonly defaultNoValueDisplayKey?: string | undefined;

    // Whether or not to show 1 row for each entities that don't have events. By default, the component WILL NOT show entities that
    // don't have any event record.
    readonly showEntitiesWithoutEvents?: boolean | undefined;

    // Controls to be displayed in the component. For now, we will only have 1 control, the Filters control. We might implement other controls later.
    readonly controls?: {
        readonly eventsFilter?: {
            // The Array of filter options to show in the Event filter dropdown
            readonly options: readonly (EventFilterOption | SELECT_INPUT_OPTION_DIVIDER_TYPE)[];
        } | undefined;
    } | undefined;

    // Whether or not to disable the table row click event. By default, when the user clicks on an event row, the component will take the user to
    // the entity details page of the entity associated with the event. Set this to false to disable that feature.
    readonly disableEventClick?: boolean;

    // The index of the view where the click event will take the user to
    readonly onClickTargetViewIndex?: number | undefined;
}


/**
 * type guard function to check if an object is a type of ViewSettingsPerMetricsDefinition
 */
const isViewSettingsPerMetricsDefinition = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityEventsTableUIComponentCustomSettings['viewSettingsPerMetricsDefinition'] => {
    const settings = obj as EntityEventsTableUIComponentCustomSettings['viewSettingsPerMetricsDefinition'];
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!(settings.showMetrics instanceof Array) || settings.showMetrics.length === 0) {
        return errorLogger?.logError({
            message: '"showMetrics" is required and must be a non-empty Array',
            value  : settings.showMetrics,
            path   : [...configPath, 'showMetrics'],
        }) ?? false;
    }

    // Every item in showMetrics must be either a string or an object of type EventMetricSettings
    if (!settings.showMetrics.every((metricSettings, index) => {
        if (typeof metricSettings !== 'string' && !isObjectLiteral(metricSettings)) {
            return errorLogger?.logError({
                message: 'Every items in "showMetrics" array must be non-empty strings or Object.',
                value  : metricSettings,
                path   : [...configPath, 'showMetrics', `[${index}]`],
            }) ?? false;
        }

        if (typeof metricSettings === 'string') {
            if (!isValidSettingStringValue(metricSettings, true)) {
                // metricSettings can just be a metric name or an Object. If it is a string, we can assume it is a metric name therefore we only
                // need to make sure it is non-empty string
                return errorLogger?.logError({
                    message: 'Value must be non-empty strings or Object.',
                    value  : metricSettings,
                    path   : [...configPath, 'showMetrics', `[${index}]`],
                }) ?? false;
            }
            return true;
        }

        // make sure the metricSettings object is also a ExtEventMetricSettings
        return isExtEventMetricSettings(metricSettings, errorLogger, [...configPath, 'showMetrics', `[${index}]`]);
    })) {
        return false;
    }

    // defaultNoValueState is optional but if it is specified, it must be one of the support eventStateNames
    const eventStateNames = Object.values(EventState);
    if (!isValidSettingStringValue(settings.defaultNoValueState, false, false, eventStateNames)) {
        return errorLogger?.logError({
            message: `"defaultNoValueState" must be undefined or one of these values [${
                eventStateNames.map((stateName) => {
                    return `"${stateName}"`;
                }).join(', ')
            }]`,
            value: settings.defaultNoValueState,
            path : [...configPath, 'defaultNoValueState'],
        }) ?? false;
    }

    // noValueStatePerMetric is optional but if it is provided, it must be a map of metricName => EventState
    if (settings.noValueStatePerMetric !== undefined) {
        if (!isObjectLiteral(settings.noValueStatePerMetric)) {
            return errorLogger?.logError({
                message: '"noValueStatePerMetric" must be undefined or an object.',
                value  : settings.noValueStatePerMetric,
                path   : [...configPath, 'noValueStatePerMetric'],
            }) ?? false;
        }

        if (!Object.entries(settings.noValueStatePerMetric).every(([metricName, state]) => {
            if (isValidSettingStringValue(state, true, false, eventStateNames)) {
                return errorLogger?.logError({
                    message: `Value must be one of the following [${
                        eventStateNames.map((stateName) => {
                            return `"${stateName}"`;
                        }).join(', ')
                    }]`,
                    value: state,
                    path : [...configPath, 'noValueStatePerMetric', metricName],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    return true;
};



/**
 * type guard function to check if an object is a type of EntityEventsTableUIComponentCustomSettings
 */
const isEntityEventsTableUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityEventsTableUIComponentCustomSettings => {

    const settings = obj as EntityEventsTableUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey, true)) {
        return errorLogger?.logError({
            message: '"titleKey" is required and must be a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.childrenClass)) {
        return errorLogger?.logError({
            message: '"childrenClass" must be undefined or a non-empty string.',
            value  : settings.childrenClass,
            path   : [...configPath, 'childrenClass'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showTableHeader)) {
        return errorLogger?.logError({
            message: '"showTableHeader" must be undefined or a boolean',
            value  : settings.showTableHeader,
            path   : [...configPath, 'showTableHeader'],
        }) ?? false;
    }

    if (settings.eventsContainerStyle && !isObjectLiteral(settings.eventsContainerStyle)) {
        return errorLogger?.logError({
            message: '"eventsContainerStyle" must be undefined or a CSSProperties an Object',
            value  : settings.eventsContainerStyle,
            path   : [...configPath, 'eventsContainerStyle'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.highlightRow)) {
        return errorLogger?.logError({
            message: '"highlightRow" must be undefined or a boolean',
            value  : settings.highlightRow,
            path   : [...configPath, 'highlightRow'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.groupRecordsByEntity)) {
        return errorLogger?.logError({
            message: '"groupRecordsByEntity" must be undefined or a boolean',
            value  : settings.groupRecordsByEntity,
            path   : [...configPath, 'groupRecordsByEntity'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showEntityName)) {
        return errorLogger?.logError({
            message: '"showEntityName" must be undefined or a boolean',
            value  : settings.showEntityName,
            path   : [...configPath, 'showEntityName'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.hideRepeatedEntityName)) {
        return errorLogger?.logError({
            message: '"hideRepeatedEntityName" must be undefined or a boolean',
            value  : settings.hideRepeatedEntityName,
            path   : [...configPath, 'hideRepeatedEntityName'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.fetchEventsLimit, false, false, 5, 1000)) {
        return errorLogger?.logError({
            message: '"fetchEventsLimit" must be undefined or an integer between 5 and 1000.',
            value  : settings.fetchEventsLimit,
            path   : [...configPath, 'fetchEventsLimit'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.eventsSortOrder, false, false, Object.values(SortOrder))) {
        return errorLogger?.logError({
            message: `"eventsSortOrder" must be undefined or one of these values [${Object.values(SortOrder).map((value) => {
                return `"${value}"`;
            }).join(', ')}].`,
            value: settings.eventsSortOrder,
            path : [...configPath, 'eventsSortOrder'],
        }) ?? false;
    }

    // showEntitiesWithoutEvents is optional but if it is provided, it must be valid
    if (!isValidSettingBooleanValue(settings.showEntitiesWithoutEvents)) {
        return errorLogger?.logError({
            message: '"showEntitiesWithoutEvents" must be undefined or a boolean.',
            value  : settings.showEntitiesWithoutEvents,
            path   : [...configPath, 'showEntitiesWithoutEvents'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultNoValueDisplayKey, false, true)) {
        return errorLogger?.logError({
            message: '"defaultNoValueDisplayKey" must be undefined or a non-empty string.',
            value  : settings.defaultNoValueDisplayKey,
            path   : [...configPath, 'defaultNoValueDisplayKey'],
        }) ?? false;
    }

    if (!settings.viewSettingsPerMetricsDefinition || !isObjectLiteral(settings.viewSettingsPerMetricsDefinition)) {
        return errorLogger?.logError({
            message: '"viewSettingsPerMetricsDefinition" is required and must be an object.',
            value  : settings.viewSettingsPerMetricsDefinition,
            path   : [...configPath, 'viewSettingsPerMetricsDefinition'],
        }) ?? false;
    }

    if (!Object.entries(settings.viewSettingsPerMetricsDefinition).every(([metricsDefId, metricsViewSettings]) => {
        if (!isViewSettingsPerMetricsDefinition(
            metricsViewSettings, errorLogger, [...configPath, 'viewSettingsPerMetricsDefinition', metricsDefId],
        )) {
            return false;
        }
        return true;
    })) {
        return false;
    }

    // defaultFiltersPerMetricsDefinition is optional but if it is provided, it must be valid
    if (settings.defaultFiltersPerMetricsDefinition !== undefined) {
        if (!isObjectLiteral(settings.defaultFiltersPerMetricsDefinition)) {
            return errorLogger?.logError({
                message: '"defaultFiltersPerMetricsDefinition" must be undefined or an object.',
                value  : settings.defaultFiltersPerMetricsDefinition,
                path   : [...configPath, 'defaultFiltersPerMetricsDefinition'],
            }) ?? false;
        }

        if (!Object.entries(settings.defaultFiltersPerMetricsDefinition).every(([metricDefId, defaultFilters]) => {
            if (!isValidSettingStringValue(defaultFilters, true)) {
                return errorLogger?.logError({
                    message: 'Every items in "defaultFiltersPerMetricsDefinition" must be non-empty string',
                    value  : defaultFilters,
                    path   : [...configPath, 'defaultFiltersPerMetricsDefinition', metricDefId],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }


    // Controls are optional but if it is provided, it must be valid
    if (settings.controls !== undefined) {
        if (!isObjectLiteral(settings.controls)) {
            return errorLogger?.logError({
                message: '"controls" must be undefined or an Object.',
                value  : settings.controls,
                path   : [...configPath, 'controls'],
            }) ?? false;
        }

        if (settings.controls.eventsFilter !== undefined) {
            if (!isObjectLiteral(settings.controls.eventsFilter)) {
                return errorLogger?.logError({
                    message: '"eventsFilter" must be undefined or an Object.',
                    value  : settings.controls.eventsFilter,
                    path   : [...configPath, 'controls', 'eventsFilter'],
                }) ?? false;
            }
            if (!(settings.controls.eventsFilter.options instanceof Array)) {
                return errorLogger?.logError({
                    message: '"options" is required and must be an Array.',
                    value  : settings.controls.eventsFilter.options,
                    path   : [...configPath, 'controls', 'eventsFilter', 'options'],
                }) ?? false;
            }
            if (!settings.controls.eventsFilter.options.every((option, index) => {
                if (typeof option === 'string') {
                    if (option !== SELECT_INPUT_OPTION_DIVIDER) {
                        return errorLogger?.logError({
                            message: `Option value must be an divider "${SELECT_INPUT_OPTION_DIVIDER}" or an Object`,
                            value  : option,
                            path   : [...configPath, 'controls', 'eventsFilter', 'options', `[${index}]`],
                        }) ?? false;
                    }
                    return true;
                }

                if (!isEventFilterOption(option, errorLogger, [...configPath, 'controls', 'eventsFilter', 'options', `[${index}]`])) {
                    return false;
                }
                return true;
            })) {
                return false;
            }
        }
    }


    if (!isValidSettingBooleanValue(settings.disableEventClick)) {
        return errorLogger?.logError({
            message: '"disableEventClick" must be undefined or a boolean',
            value  : settings.disableEventClick,
            path   : [...configPath, 'disableEventClick'],
        }) ?? false;
    }


    if (!isValidSettingNumberValue(settings.onClickTargetViewIndex, false, false, 0, 100)) {
        return errorLogger?.logError({
            message: '"onClickTargetViewIndex" must be undefined or an integer between 0 and 100',
            value  : settings.onClickTargetViewIndex,
            path   : [...configPath, 'onClickTargetViewIndex'],
        }) ?? false;
    }

    return true;
};



/**
 * These are the props required for the EntityEventsTableUIComponent
 */
export interface EntityEventsTableUIComponentProps extends EntityUIComponentWithChildrenProps {
    readonly customSettings?: EntityEventsTableUIComponentCustomSettings | undefined;
}


/**
 * This is an Entity UI Component that displays all of the children event's records based on the selected time range
 */
export const EntityEventsTableUIComponent: React.FC<EntityEventsTableUIComponentProps> = (
    props: EntityEventsTableUIComponentProps,
) => {
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const tableClasses = useModeTableStyle();
    const panelClasses = useModePanelStyle();

    /**
     * This component can be used for showing the selected entity's events or the selected entity's children's events depending on whether
     * this settings is configured "props.customSettings.childrenClass". We need to do a check "props.customSettings?.childrenClass" in multiple
     * places which can be tedious therefore, we will create a variable for this just for convenience.
     */
    const showChildrenEvents = props.customSettings?.childrenClass !== undefined;

    const [eventFilterMenuAnchorEl, isEventFilterMenuOpened, onEventFilterAnchorElClicked, onEventFilterMenuClosed] = useDropdown();

    // get all entity classes
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // Get the entity object for the entity that this component is showing
    const selectedEntity = useSelectEntity(props.projectId, props.entityId);

    /**
     * This component can show events for the selected entity OR the selected entity's children. Therefore, we need to know the target
     * entities' class. If this component is used for showing events for the selected entity then the targetEntityClass will be the selected
     * entity's entityClass. But if this component is used for showing events for it's children then the target entity class will be the
     * entity class specified in the "props.customSettings.childrenClass"
     */
    const targetEntityClass = useMemo(() => {
        return entityClasses?.find((entityClass) => {
            if (showChildrenEvents) {
                return props.customSettings.childrenClass === entityClass.entityClass;
            }
            return selectedEntity?.entityClass === entityClass.entityClass;
        });
    }, [entityClasses, showChildrenEvents, selectedEntity?.entityClass, props.customSettings?.childrenClass]);


    /**
     * Get all entities of the type props.customSettings.childrenClass BUT only do it if "showChildrenEvents" is specified.
     * Normally, we would do this
     *      const childEntities = showChildrenEvents? useSelectAllEntityChildren(...): undefined;
     *
     * However, because "useSelectAllEntityChildren" is a hook and we can't call a hook conditionally therefore we have to always call it
     * but pass undefined for all the param values if showChildrenEvents is false.
     */
    const childEntities = useSelectAllEntityChildren(
        showChildrenEvents ? props.projectId : undefined,
        showChildrenEvents ? props.entityId : undefined,
        showChildrenEvents ? {
            entityClasses: props.customSettings?.childrenClass,
        } : undefined,
    );


    /**
     * Get the list of entities we need to target. This can be the selected entity itself if "showChildrenEvents" is NOT set to true.
     * If "showChildrenEvents" is set to true then this will be the list of the selected entity's children.
     */
    const targetEntities = useMemo(() => {
        if (showChildrenEvents) {
            return childEntities;
        }

        if (selectedEntity) {
            return [selectedEntity];
        }

        return [];
    }, [showChildrenEvents, selectedEntity, childEntities]);



    /**
     * Create the params that are required for fetching data based on the props.fetchDataParams. Since this component need to fetch data
     * based on some start/end time, it will convert the props.fetchDataParams to FetchDataByTimeRangeOptions.
     */
    const fetchDataTimeRange = useConvertTimeControlParamsToFetchDataTimeRangeParams(props.fetchDataParams);


    /**
     * The state that stores all the info we need to make API call to fetch data
     */
    const [fetchDataParams, setFetchedDataParams] = useState<{
        readonly homeId?: number | undefined;
        readonly startTime?: number | undefined;
        readonly endTime?: number | undefined;

        // Used for pagination
        readonly limit: number;
        readonly sortOrder: SortOrder;
        readonly currentPage: number;
        readonly filters: readonly EventFilterOption[];   // This is the selected filters by the user
    }>({
        limit      : props.customSettings?.fetchEventsLimit || ITEMS_PER_PAGE_10,
        sortOrder  : props.customSettings?.eventsSortOrder ?? SortOrder.DESC,
        currentPage: 0,
        filters    : [],
    });


    /**
     * This useEffect is used for resetting the "fetchDataParams" when some of the params changed.
     */
    useEffect(() => {
        setFetchedDataParams((currentData) => {
            return produce(currentData, (draft: Draft<typeof currentData>) => {
                draft.startTime = fetchDataTimeRange?.startTime;
                draft.endTime = fetchDataTimeRange?.endTime;
                draft.homeId = selectedEntity?.homeId;
                draft.currentPage = 0;
            });
        });
    }, [fetchDataTimeRange?.endTime, fetchDataTimeRange?.startTime, selectedEntity?.homeId]);


    /**
     * Get all the HOME_APP_PROXY metrics definitions that we need to fetch data for based on the "viewSettingsPerMetricsDefinition"
     * An entity can have many metrics definitions but we need the metrics definition of type "HOME_APP_PROXY". And within the
     * set of "HOME_APP_PROXY" metrics definitions, we only need to fetch data for the metrics definition that is configured in
     * "viewSettingsPerMetricsDefinition". The metrics definition specified in the "viewSettingsPerMetricsDefinition" are the list
     * of metrics that we need to show therefore we only need to fetch data for those metrics definitions.
     */
    const metricsDefinitions = useMemo(() => {
        if (targetEntityClass) {
            // filter out metrics definitions based on the customSettings' metrics exclusion settings
            return targetEntityClass.metricsDefinitions?.filter((metricsDef) => {
                return metricsDef.dataStoreConfiguration.moduleType === EntityModuleType.HOME_APP_PROXY
                            && props.customSettings?.viewSettingsPerMetricsDefinition[metricsDef.metricsDefinitionId];
            });
        }

        return undefined;
    }, [targetEntityClass, props.customSettings?.viewSettingsPerMetricsDefinition]);



    /**
     * Create a map of event record searchKey => entity
     * Each record contains a searchKey and we need to use that searchKey to map it back to the entity that the record belongs to.
     * We will need this map later for looking up entities based on searchKey. This map allow us to quickly lookup entity and metrics definition
     * by searchKey. This is what the object looks like
     *
     *      searchKeyToEntityMap = {
     *          "driver::driver1::alcohol_test_result_event": ENTITY,
     *          "driver::driver8::vehicle_event": ENTITY,
     *      }
     */
    const searchKeyToEntityMap = useMemo(() => {
        const result: {
            [searchKey: string]: Entity;
        } = {};
        targetEntities?.forEach((entity) => {
            metricsDefinitions?.forEach((metricsDef) => {
                const searchKey = getMetricsDefinitionMetricsId(entity, metricsDef.metricsDefinitionId);
                result[searchKey] = entity;
            });
        });
        return result;
    }, [targetEntities, metricsDefinitions]);



    /**
     * Call the "useGetEventLogData" to get the event logs for all entities, metrics definitions, and pages
     */
    const { eventLogRecords, hasMoreRecords } = useGetEventLogData(
        fetchDataParams.homeId,
        targetEntities,
        metricsDefinitions,
        fetchDataParams.startTime,
        fetchDataParams.endTime,
        fetchDataParams.limit,
        fetchDataParams.sortOrder,
        fetchDataParams.currentPage,
        fetchDataParams.filters,
        props.customSettings?.defaultFiltersPerMetricsDefinition,
    );



    /**
     * On Load More button clicked, we will increase the fetchDataParam.currentPage value to the next page
     */
    const fetchMoreEvents = useCallback(() => {
        setFetchedDataParams((currentData) => {
            return produce(currentData, (draft: Draft<typeof currentData>) => {
                draft.currentPage = currentData.currentPage + 1;
            });
        });
    }, []);



    /**
     * "fetchDataParams.eventRecords" is an array of records and they are sorted by the current sortOrder. If customSettings.groupRecordsByEntity
     * is FALSE, we will display these records as is in that order. However, if "groupRecordsByEntity" is true, we will group the records
     * by the entity they are associated with and then joins the records of all the group into 1 single array.
     * For example: Let say we have this records and we need to group them
     *
     *      eventRecords: [a, b, c, b, a, b, a, c, d, a]
     *
     *      We will group them like this
     *          recordsByEntityId: {
     *              a: [a, a, a, a],
     *              b: [b, b, b],
     *              c: [c, c]
     *              d: [d]
     *          }
     *
     *      And then we flatten the groups and return the records as a flat array
     *          return [a, a, a, a, b, b, b, c, c, d, d]
     */
    const groupedRecords = useMemo((): readonly EntityEventLogRecord[] => {

        // First, convert each EventLogRecord to EntityEventLogRecord by adding "entityId" to the record. Each record is associated with
        // an entity. We will need this entityId so that we can map event record to an entity.
        const records: EntityEventLogRecord[] = [];
        Object.values(eventLogRecords).forEach((record) => {
            // Find the child entity based on the record's searchKey. NOTE, the record can contain more than 1 searchKeys
            const matchedSearchKey = record.searchKeys?.find((searchKey) => {
                return searchKeyToEntityMap[searchKey] !== undefined;
            });

            const childEntity = matchedSearchKey ? searchKeyToEntityMap[matchedSearchKey] : undefined;

            if (matchedSearchKey && childEntity) {
                records.push({
                    ...record,
                    entityId: childEntity.entityId,
                });
            }
        });


        // if "groupRecordsByEntity", group the records into groups
        if (props.customSettings?.groupRecordsByEntity) {

            // Go though each record and add them to this "recordsByEntityId" to group them by entityId
            const recordsByEntityId: {
                [entityId: string]: EntityEventLogRecord[];
            } = {};

            Object.values(records).forEach((record) => {
                // Find the child entity based on the searchKey
                const matchedSearchKey = record.searchKeys?.find((searchKey) => {
                    return searchKeyToEntityMap[searchKey] !== undefined;
                });

                const childEntity = matchedSearchKey ? searchKeyToEntityMap[matchedSearchKey] : undefined;
                if (childEntity) {
                    // Get the record group for the childEntityId, create a new group it one does not exist, and then add the record to that group
                    const recordsGroup = recordsByEntityId[childEntity.entityId] ?? [];
                    recordsByEntityId[childEntity.entityId] = recordsGroup;

                    recordsGroup.push(record);
                }
            });

            // Then combine the array of records from all the groups and return them as 1 flat array of records.
            return Object.values(recordsByEntityId).flat();
        }


        // if NOT groupRecordsByEntity then return the array of records as is.
        return records;
    }, [eventLogRecords, props.customSettings?.groupRecordsByEntity, searchKeyToEntityMap]);


    /**
     * Once we have the grouped records, we can convert each record to data objects that the component can display. For example, we need to
     * convert the timestamp local time display, the record's entityId to entityName. Format the metrics' values and compute the state
     * for each metric. Once we have these records, we can just loop through them and display their data, no more any other transformation.
     */
    const localizedRecords = useMemo((): readonly LocalizedEventLogRecord[] => {

        const customSettings = props.customSettings;
        if (!customSettings) {
            return [];
        }

        const entitiesWithEvents: {
            [entityId: string]: boolean
        } = {};

        const result: LocalizedEventLogRecord[] = [];

        groupedRecords.forEach((record) => {
            // Look up the entity using the record.entityId
            const entity = targetEntities?.find((child) => {
                return child.entityId === record.entityId;
            });

            if (entity) {
                // Remember that the entity has event. We will use this info later
                entitiesWithEvents[record.entityId] = true;

                const localizedRecord = createLocalizedEventLogRecord(
                    record, entity, metricsDefinitions, customSettings, trans,
                );
                result.push(localizedRecord);
            }
        });


        // If "showEntitiesWithoutEvents" is true AND there is no filter applied then we need to loop through the child entities that DON'T have
        // event and create a localizedEventRecord for them as well
        if (
            customSettings.showEntitiesWithoutEvents
            && fetchDataParams.filters.length === 0
            && customSettings.defaultFiltersPerMetricsDefinition === undefined
        ) {
            targetEntities?.forEach((child) => {
                if (!entitiesWithEvents[child.entityId]) {
                    const localizedRecord = createLocalizedEventLogRecord(
                        undefined, child, metricsDefinitions, customSettings, trans,
                    );
                    result.push(localizedRecord);
                }
            });
        }

        return result;
    }, [props.customSettings, groupedRecords, fetchDataParams.filters.length, targetEntities, metricsDefinitions,
        trans]);


    // If there is a setting for "controls.eventsFilter.options", get the list of filter options that are not disabled
    const visibleEventsFilterOptions = useMemo(() => {
        return props.customSettings?.controls?.eventsFilter?.options.filter((option) => {
            return option === SELECT_INPUT_OPTION_DIVIDER || !option.hidden;
        });
    }, [props.customSettings?.controls?.eventsFilter?.options]);


    // Check the "visibleEventsFilterOptions" and automatically mark the filter options that has "selected" set to true
    useEffect(() => {

        // Find the options that has selected set to true
        const selectedOptions: EventFilterOption[] = [];
        visibleEventsFilterOptions?.forEach((option) => {
            if (isEventFilterOption(option) && option.selected) {
                selectedOptions.push(option);
            }
        });

        if (selectedOptions.length > 0) {
            setFetchedDataParams((currentValue) => {
                return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                    draft.filters = selectedOptions;

                    // Because filter changed, we will have to reset the pagination and fetched events
                    draft.currentPage = 0;
                });
            });
        }
    }, [visibleEventsFilterOptions]);


    /**
     * On toggle an event filter. This will add the specified filter to the list of filters if it isn't already added to the list. If it is
     * already in the list, this will remove the filter.
     */
    const onEventsFilterToggled = useCallback((filter: EventFilterOption) => {
        setFetchedDataParams((currentValue) => {
            return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                if (currentValue.filters.includes(filter)) {
                    // If the filter is already existed in the list, remove the filter
                    draft.filters = currentValue.filters.filter((existingFilter) => {
                        return existingFilter !== filter;
                    });
                } else {
                    draft.filters.push(filter);
                }

                // Because filter changed, we will have to reset the pagination and fetched events
                draft.currentPage = 0;
            });
        });
    }, []);


    /**
     * On clear selected event filters and show all events
     */
    const onClearEventsFilters = useCallback(() => {
        setFetchedDataParams((currentValue) => {
            return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                draft.filters = [];

                // Because filter changed, we will have to reset the pagination and fetched events
                draft.currentPage = 0;
            });
        });
    }, []);


    return (
        <>
            {targetEntities && targetEntities.length > 0 && (
                <Paper elevation={2} className={clsx(props.className, classes.root)} style={props.style}>
                    <div className={clsx(panelClasses.panelHeader, classes.panelHeader)}>
                        <div className={panelClasses.panelHeaderLeft}>
                            <div className={panelClasses.panelHeaderTitle}>
                                <Typography variant="h5">
                                    {props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : ''}
                                </Typography>
                            </div>
                        </div>
                        {visibleEventsFilterOptions && visibleEventsFilterOptions.length > 0 && (
                            <div className={panelClasses.panelHeaderRight}>
                                <IconButton
                                    className={clsx(panelClasses.panelHeaderCustomPrimaryAction)}
                                    onClick={(event) => {
                                        onEventFilterAnchorElClicked(event);
                                    }}
                                >
                                    <FontIcon iconName="filter_alt" />
                                </IconButton>
                                <Menu
                                    getContentAnchorEl={null}
                                    anchorEl={eventFilterMenuAnchorEl}
                                    anchorOrigin={{
                                        vertical  : 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical  : 'top',
                                        horizontal: 'right',
                                    }}
                                    keepMounted
                                    open={isEventFilterMenuOpened}
                                    onClose={onEventFilterMenuClosed}
                                >
                                    <MenuItem
                                        key="all_option"
                                        className={classes.filterOptionLabel}
                                        selected={fetchDataParams.filters.length === 0}
                                        onClick={() => {
                                            if (fetchDataParams.filters.length > 0) {
                                                onClearEventsFilters();
                                            }
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Checkbox
                                                color="primary"
                                                checked={fetchDataParams.filters.length === 0}
                                                size="small"
                                            />
                                        </ListItemIcon>
                                        {trans('label.all')}
                                    </MenuItem>
                                    {visibleEventsFilterOptions.map((option, index) => {
                                        if (isEventFilterOption(option)) {
                                            return (
                                                <MenuItem
                                                    key={option.labelKey}
                                                    className={classes.filterOptionLabel}
                                                    selected={fetchDataParams.filters.includes(option)}
                                                    onClick={() => {
                                                        onEventsFilterToggled(option);
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <Checkbox
                                                            color="primary"
                                                            checked={fetchDataParams.filters.includes(option)}
                                                            size="small"
                                                        />
                                                    </ListItemIcon>
                                                    {trans(option.labelKey)}
                                                </MenuItem>
                                            );
                                        }

                                        // If an option has no label or value are treated as dividers
                                        return (
                                        // eslint-disable-next-line react/no-array-index-key
                                            <Divider key={`divider-${index}`} />
                                        );
                                    })}
                                </Menu>
                            </div>
                        )}
                    </div>
                    <div
                        className={clsx(panelClasses.panelContent, classes.panelContent)}
                        style={props.customSettings?.eventsContainerStyle}
                    >
                        {localizedRecords.length > 0 && (
                            <Table className={tableClasses.root}>

                                {/* If "showTableHeader" is set to true, create the Table Header row */}
                                {props.customSettings?.showTableHeader && (
                                    <TableHead className={tableClasses.tableHeader}>
                                        <TableRow className={tableClasses.tableHeaderRow}>
                                            {props.customSettings?.showEntityName !== false && (
                                                <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'entity-name')}>
                                                    {capitalizeAll(trans('label.name'))}
                                                </TableCell>
                                            )}
                                            <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'timestamp')}>
                                                {capitalizeAll(trans('label.date'))}
                                            </TableCell>

                                            {/*
                                                Create one column header for each metric we need to show. Each "settingsPerMetricsDefinition"
                                                contains a list of metrics to be shown for that metric definition.
                                            */}
                                            {Object.entries(props.customSettings?.viewSettingsPerMetricsDefinition ?? {}).map((
                                                [metricsDefId, metricsViewSettings],
                                            ) => {
                                                const columns: React.ReactElement[] = [];

                                                // Get the list of metrics we need to show for the current metricsDefinition and create 1 column for
                                                // each metric
                                                const showMetrics = metricsViewSettings?.showMetrics;
                                                showMetrics?.forEach((metricSettings) => {

                                                    const metricSettingsObj: ExtEventMetricSettings = typeof metricSettings === 'string'
                                                        ? { name: metricSettings }
                                                        : metricSettings;
                                                    const metricsDefinition = metricsDefinitions?.find((metricsDef) => {
                                                        return metricsDef.metricsDefinitionId === metricsDefId;
                                                    });
                                                    const metric = metricsDefinition ? findMetricInfo(metricsDefinition, metricSettingsObj) : undefined;

                                                    const displayName = metric
                                                        ? getEventMetricDisplayName(metric, metricSettingsObj, trans)
                                                        : metricSettingsObj.name;

                                                    columns.push(
                                                        <TableCell
                                                            key={metricSettingsObj.name}
                                                            className={clsx(tableClasses.tableCol, classes.tableCol, 'metric-value')}
                                                            style={metricSettingsObj.headerCellStyle}
                                                        >
                                                            {capitalizeAll(displayName)}
                                                        </TableCell>,
                                                    );
                                                });

                                                return columns;
                                            })}
                                        </TableRow>
                                    </TableHead>
                                )}

                                <TableBody>
                                    {localizedRecords.map((record, index) => {

                                        // If "hideRepeatedEntityName" is true AND if the current record's entityId is the same as the
                                        // previous record's entityId then don't display the entityName again.
                                        const hideRepeatingEntityName = index > 0 && record.entityId === localizedRecords[index - 1].entityId
                                            && props.customSettings?.hideRepeatedEntityName === true;


                                        return (
                                            <TableRow
                                                key={`${`${record.entityId}-${record.displayTimestamp}`}`}
                                                className={clsx(
                                                    tableClasses.tableBodyRow, classes.tableBodyRow,
                                                    props.customSettings?.highlightRow && record.recordAggregatedState && 'highlight',
                                                    record.recordAggregatedState,
                                                )}
                                                onClick={props.customSettings?.disableEventClick !== true ? () => {
                                                    if (props.onShowEntityDetails) {
                                                        if (record.timestamp) {
                                                            const startTime = moment(record.timestamp).valueOf();
                                                            // Record doesn't have endTime therefore we will just use 1 hour from the start time.
                                                            const endTime = startTime + Time.HOUR_IN_MS;
                                                            props.onShowEntityDetails(
                                                                record.entityId,
                                                                props.customSettings?.onClickTargetViewIndex,
                                                                startTime,
                                                                endTime,
                                                            );
                                                        } else {
                                                            props.onShowEntityDetails(
                                                                record.entityId,
                                                                props.customSettings?.onClickTargetViewIndex,
                                                            );
                                                        }
                                                    }
                                                } : undefined}
                                            >
                                                {props.customSettings?.showEntityName !== false && (
                                                    <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'entity-name')}>
                                                        {!hideRepeatingEntityName && (
                                                            record.entityName
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'timestamp')}>
                                                    {record.displayTimestamp}
                                                </TableCell>

                                                {/*
                                                    Create one column for each metric we need to show. Each "settingsPerMetricsDefinition"
                                                    contains a list of metrics to be shown for that metric definition.
                                                */}
                                                {Object.entries(props.customSettings?.viewSettingsPerMetricsDefinition ?? {}).map((
                                                    [metricsDefId, metricsViewSettings],
                                                ) => {
                                                    const columns: React.ReactElement[] = [];

                                                    const showMetrics = metricsViewSettings?.showMetrics;
                                                    showMetrics?.forEach((metricSettings) => {
                                                        // metricSettings can be a string or an object. If it is a string, that string is the name of the metric we need to show.
                                                        // We can create a NEW ExtEventMetricSettings object using that value as the metric name. If 'metricSettings' is an object
                                                        // then we can use it as is, no need to do anything.
                                                        const metricSettingsObj: ExtEventMetricSettings = typeof metricSettings === 'string'
                                                            ? {
                                                                name: metricSettings,
                                                            } : metricSettings;

                                                        const metricName = metricSettingsObj.name;
                                                        columns.push(
                                                            <TableCell
                                                                key={metricName}
                                                                className={clsx(
                                                                    tableClasses.tableCol, classes.tableCol, 'metric-value',
                                                                    !props.customSettings?.highlightRow
                                                                        && record.statePerMetricsDefinition[metricsDefId]?.[metricName] && 'highlight',
                                                                    record.statePerMetricsDefinition[metricsDefId]?.[metricName],
                                                                )}
                                                                style={metricSettingsObj.valueCellStyle}
                                                            >
                                                                <div className="value" style={metricSettingsObj.valueStyle}>
                                                                    {
                                                                        record.displayValuePerMetricsDefinition[metricsDefId]?.[metricName]
                                                                        ?? props.customSettings?.defaultNoValueDisplayKey
                                                                    }
                                                                </div>
                                                            </TableCell>,
                                                        );
                                                    });

                                                    return columns;
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}

                        {localizedRecords.length === 0 && (
                            <GenericMessageBox
                                title={trans('generic.no_data.title', {
                                    term: capitalizeAll(trans('label.data', { count: 2 })),
                                })}
                            />
                        )}
                    </div>

                    {/* Add the "Load More" button if there are more events to load */}
                    {hasMoreRecords && (
                        <div className={classes.loadMoreButtonContainer}>
                            <Button
                                className={classes.loadMoreButton}
                                variant="contained"
                                color="primary"
                                onClick={fetchMoreEvents}
                            >
                                {trans('label.load_more')}
                            </Button>
                        </div>
                    )}
                </Paper>
            )}

            {!targetEntities && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityEventsTableUIComponent', EntityEventsTableUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Events_Table_Comp',
    {
        componentFunc          : EntityEventsTableUIComponent,
        customSettingsValidator: isEntityEventsTableUIComponentCustomSettings,
    },
);
